import { useRef } from 'react';

import { Button } from 'components/Button/Button';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { useLongPress } from 'hooks/useLongPress';
import type { CmsApiReactionType } from 'modules/cms/api/reactions/types/CmsApiReactionType';
import { REACTIONS } from 'modules/cms/constants/reactions';
import { getAnimatedIconNameForReactionType } from 'modules/cms/helpers/reactions/getAnimatedIconNameForReactionType';

import { CmsReactionButtonContainer } from './CmsReactionButton.styled';
import { CmsReactionButtonTooltip } from './CmsReactionButtonTooltip';
import { useCmsReactionsContext } from './CmsReactionsContext';

type ReactionTooltipRef = {
  closeTooltip: () => void;
  openTooltip: () => void;
};

type Props = {
  onReactionClick: (reactionType: CmsApiReactionType) => void;
};

export function CmsReactionButton({ onReactionClick }: Props) {
  const { ownReaction } = useCmsReactionsContext();

  const reactionTooltipRef = useRef<ReactionTooltipRef>(null);

  const { onTouchEnd, onTouchMove, onTouchStart } = useLongPress(() => {
    if (reactionTooltipRef.current) {
      reactionTooltipRef.current.openTooltip();
    }
  });

  const handleClick = () => {
    onReactionClick(ownReaction?.reactionType || 'LIKE');

    if (reactionTooltipRef.current) {
      reactionTooltipRef.current.closeTooltip();
    }
  };

  return (
    <CmsReactionButtonContainer
      data-qa-id="cms-reaction-button"
      data-qa-reaction-status={ownReaction?.reactionType}
    >
      <Tooltip
        ref={reactionTooltipRef}
        content={<CmsReactionButtonTooltip onClickReaction={onReactionClick} />}
        size="small"
        variant="filled"
        placement="top-center"
        animate
      >
        <Button
          type="button"
          onClick={handleClick}
          onTouchEnd={onTouchEnd}
          onTouchMove={onTouchMove}
          onTouchStart={onTouchStart}
          variant={
            ownReaction?.reactionType
              ? REACTIONS[ownReaction.reactionType].buttonVariant
              : 'secondary'
          }
          icon={
            ownReaction?.reactionType
              ? {
                  type: 'animated',
                  name: getAnimatedIconNameForReactionType(
                    ownReaction.reactionType,
                  ),
                  options: { maxLoopCount: 2 },
                }
              : {
                  type: 'normalized',
                  name: 'thumbs-up',
                }
          }
        >
          {ownReaction?.reactionType
            ? REACTIONS[ownReaction.reactionType].text
            : getText('Like')}
        </Button>
      </Tooltip>
    </CmsReactionButtonContainer>
  );
}
